export class AvatarFactory {
  private static readonly DEFAULT_COLORS = [
    '#1ABC9C',
    '#3498DB',
    '#6699CC',
    '#F1C40F',
    '#8E44AD',
    '#B45B3E',
    '#E74C3C',
    '#D35400',
    '#479AC7',
    '#2C3E50',
    '#7F8C8D',
    '#336699',
    '#66CCCC',
    '#00B271'
  ];
  private static readonly AVATAR_DATA_URL_CACHE = new Map<string, string>();

  /**
   * 生成默认头像
   * @param name 用户名
   * @returns 头像的 Data URL
   */
  public static defaultAvatarWithName(name: string): string {
    const cacheKey = String(name ?? '');
    const cached = this.AVATAR_DATA_URL_CACHE.get(cacheKey);
    if (cached) {
      return cached;
    }

    const avatarName = this.createAvatarNameWithOriginName(name);
    const backgroundColor = this.generateRandomColorOfAvatarName(avatarName);
    const size = { width: 320, height: 320 };
    const textColor = '#FFFFFF';
    const fontSize = this.isChinese(name) ? 140 : 140;
    const fontFamily = this.isChinese(name) ? 'PingFang TC, sans-serif' : 'SF Pro Rounded, sans-serif';

    const dataUrl = this.avatarWithText(avatarName, size, backgroundColor, textColor, fontSize, fontFamily);
    this.AVATAR_DATA_URL_CACHE.set(cacheKey, dataUrl);
    return dataUrl;
  }

  /**
   * 生成文字头像
   * @param text 显示文字
   * @param size 尺寸
   * @param backgroundColor 背景色
   * @param textColor 文字颜色
   * @param fontSize 字体大小
   * @param fontFamily 字体族
   * @returns 头像的 Data URL
   */
  public static avatarWithText(
    text: string,
    size: { width: number; height: number },
    backgroundColor: string,
    textColor: string,
    fontSize: number,
    fontFamily: string
  ): string {
    // 创建 Canvas 元素
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d')!;

    // 设置高DPI支持
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = size.width * devicePixelRatio;
    canvas.height = size.height * devicePixelRatio;
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    ctx.scale(devicePixelRatio, devicePixelRatio);

    // 绘制背景
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, size.width, size.height);

    // 设置文字样式
    ctx.fillStyle = textColor;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 绘制文字（居中）
    const centerX = size.width / 2;
    const centerY = size.height / 2;
    ctx.fillText(text, centerX, centerY);

    // 返回 Data URL
    return canvas.toDataURL('image/png');
  }

  /**
   * 从原始名称创建头像名称
   * @param name 原始名称
   * @returns 头像显示名称
   */
  private static createAvatarNameWithOriginName(name: string): string {
    // 添加空值检查
    if (!name || typeof name !== 'string') {
      return 'U';
    }
    const text = name.trim();
    if (text.length === 0) {
      return 'U';
    }

    const separators = [' ', '-', '_', '(', '.'];
    let splitArray: string[] = [];

    // 尝试用分隔符分割
    for (const separator of separators) {
      if (text.includes(separator)) {
        splitArray = text.split(separator).filter(part => part.length > 0);
        if (splitArray.length >= 2) {
          break;
        }
      }
    }

    if (splitArray.length === 0) {
      // 没有分隔符，取前两个字符
      return text.substring(0, 2).toUpperCase();
    } else {
      // 有分隔符，取前两个部分的首字符
      const newName = splitArray
        .slice(0, 2)
        .map(part => part.substring(0, 1))
        .join('')
        .toUpperCase();
      return newName;
    }
  }

  /**
   * 根据名称生成随机颜色
   * @param name 名称
   * @returns 颜色值
   */
  private static generateRandomColorOfAvatarName(name: string): string {
    const asciiCode = this.generateASCIICodeOfText(name);
    const index = asciiCode % this.DEFAULT_COLORS.length;
    return this.DEFAULT_COLORS[index];
  }

  /**
   * 生成文本的ASCII码总和
   * @param text 文本
   * @returns ASCII码总和
   */
  private static generateASCIICodeOfText(text: string): number {
    let code = 0;
    for (let i = 0; i < text.length; i++) {
      code += text.charCodeAt(i);
    }
    return code;
  }

  /**
   * 判断是否包含中文字符
   * @param text 文本
   * @returns 是否包含中文
   */
  private static isChinese(text: string): boolean {
    return /[\u4e00-\u9fff]/.test(text);
  }
}
